import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { TooltipProps } from '@mui/material/Tooltip';
import type { ReactNode } from 'react';

import { Ellipsis } from '../Ellipsis/Ellipsis.tsx';
import { HighlightedPhrase } from '../HighlightedPhrase/HighlightedPhrase.tsx';

const getItemPadding = (level?: number | string) => (level ? (Number(level) - 1) * 2 : 0);

interface AutoCompleteRenderOptionsProps {
  option: string;
  phraseToHighlight?: string;
  dataTest?: string;
  children?: ReactNode;
  childrenLocation?: 'above' | 'below';
  getPaddingLevel?: () => number | string | undefined;
  tooltip?: boolean;
  hasMultiple?: boolean;
  selected?: boolean;
  tooltipPlacement?: TooltipProps['placement'];
}

export const AutoCompleteRenderOptions = ({
  children,
  option,
  childrenLocation = 'below',
  phraseToHighlight,
  dataTest,
  getPaddingLevel,
  tooltip = false,
  hasMultiple = false,
  selected = false,
  tooltipPlacement,
}: AutoCompleteRenderOptionsProps) => {
  const render = () => (
    <>
      {childrenLocation === 'above' && children}
      <HighlightedPhrase searchPhrase={phraseToHighlight}>{option}</HighlightedPhrase>
      {childrenLocation === 'below' && children}
    </>
  );

  const Icon = selected ? RemoveIcon : AddIcon;

  if (getPaddingLevel) {
    return (
      <Box
        data-test={dataTest}
        sx={{
          paddingLeft: getItemPadding(getPaddingLevel()),
          display: 'flex',
          justifyContent: 'space-between',
          flex: 1,
        }}
      >
        <Ellipsis width="100%" tooltip={tooltip} tooltipPlacement={tooltipPlacement}>
          {render()}
        </Ellipsis>
        {hasMultiple && (
          <IconButton size="small">
            <Icon />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
      <Ellipsis width="100%" tooltip={tooltip} data-test={dataTest} tooltipPlacement={tooltipPlacement}>
        {render()}
      </Ellipsis>
      {hasMultiple && (
        <IconButton size="small">
          <Icon />
        </IconButton>
      )}
    </Box>
  );
};
